import { useMemo, useEffect, useRef, useState } from "react";

// 위험성 평가 커스텀 훅
import { useGetTableDataInQueryString } from "../../RISK_ASSESSMENT/components/Table/hooks";

import dayjs from "dayjs";
import * as echarts from 'echarts'; // ECharts 3.0.2 호환
import { AgGridReact } from "ag-grid-react";
import myTheme from "../../common/DataGrid/ag-grid-theme-builder";

import {
  alpha,
  Box, Button,
  Card, CardContent, CardHeader, Collapse,
  Drawer, Divider, Dialog, DialogContent, DialogActions,
  IconButton,
  Typography,
  useTheme, useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import CloseIcon from "@mui/icons-material/Close";
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RemoveIcon from '@mui/icons-material/Remove';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";

import { CollapsibleCardTemplate } from "../../RISK_ASSESSMENT/components/common/HelperFunc";
import { FONT_PROPS } from "../../../styles/colors";

// styled
const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
}));

const DrawerTitle = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
}));

const DrawerContent = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

// mapper
// const machineTotalRiskColors = {
//   매우낮음: '#D5FFD5',
//   낮음: '#B8FFB8',
//   보통: '#FBFF00',
//   약간높음: '#FFD5B8',
//   높음: '#FF9900',
//   매우높음: '#FF4444',
//   미평가: '#C8C8DA'
// };
// const riskEvaluationColors = {
//   하: '#00A600',
//   중: '#FFD400',
//   상: '#DB0000',
// };
const collapseTitleSX = {
  px: 3, 
  py: 1.5, 
  bgcolor: alpha("#111111", 0.5), 
  fontSize: '1em', 
  fontWeight: FONT_PROPS.REGULAR, 
  color: '#ffffff', 
  borderBottom: `1px solid ${alpha("#ffffff", 0.3)}`,
  '&:hover': {
    bgcolor: alpha("#111111", 0.7)
  },
};
const collapseContainerSX = {
  boxShadow: 'none', 
  border: 'none', 
  borderRadius: 0, 
  bgcolor: 'transparent',
  mb: 0, 
};
const collapseContentSX = {
  p: 0, 
  "&:last-child": { pb: 0 }
};
const collapseIconSX = {
  // 아이콘 공통 스타일
  "& .MuiSvgIcon-root": {
    fontSize: 24,                 // 크기
    color: "#ffffff",             // 색상
    transition: "color .2s, transform .2s",
  },
  // 호버 시 아이콘만 효과
  "&:hover .MuiSvgIcon-root": {
    color: alpha("#ffffff", 0.8),
    transform: "scale(1.1)",
  },
};

// 위험성 평가 상세정보 창 컴포넌트
export function RiskLevelDetails({
  isOpen = false,
  onClose= null,
  group_code,
  project_code,
  currentMchId,
  referenceDate,
}) {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));      // 폭 기준
  const isShort = useMediaQuery("(max-height: 1080px)");          // 높이 기준

  const top = useMemo(() => {
    if (isShort) return 50;
    return isMdUp ? 33 : 56;
  }, [isMdUp, isShort]);

  // TODO: 드로어 고정 기능 추후 구현
  // const [fixedDrawer, setFixedDrawer] = useState(false); // 드로어 고정 여부
  // const handleSetFixedDrawer = () => setFixedDrawer((prev => !prev));

  // // prop이 오면 그걸 쓰고, 없으면 "마운트 시점의" 시간을 한 번만 고정
  // const stableRefDate = useRef(
  //   referenceDate ?? dayjs().format("YYYY-MM-DD HH:mm:ss")
  // ).current;

  // 장비 정보 데이터 조회
  const getMachineInfo = useGetTableDataInQueryString(
    "request",
    "/getMachineInfo",
    group_code,
    project_code,
    { params: { dt_sync_id: currentMchId } } // 장비 id로 데이터 조회
  );

  // 위험성 평가 정보(상.중.하) 데이터 조회
  const getRiskLowMiddleHigh = useGetTableDataInQueryString(
    "request",
    "/getRiskLowMiddleHigh",
    group_code,
    project_code,
    { params: { dateParam: referenceDate, dt_sync_id: currentMchId } } // 장비 id로 데이터 조회
  );

  // 설비별 종합 위험성 지표 조회 요청
  const getMachineTotalRiskData = useGetTableDataInQueryString(
    "request",
    "/getMachineTotalRiskData",
    group_code,
    project_code,
    { params: { dateParam: referenceDate, dt_sync_id: currentMchId } },
  );

  // 개선 대책 목록, 위험성 평가 목록 데이터 조회
  const getStrategyAndEvaluationData = useGetTableDataInQueryString(
    "request",
    "/getStrategyAndEvaluationData",
    group_code,
    project_code,
    { params: { dateParam: referenceDate, dt_sync_id: currentMchId } } // 장비 id로 데이터 조회
  );
  const machineInfo = getMachineInfo.data ?? [];
  const riskLowMiddleHighData = getRiskLowMiddleHigh.data ?? [];
  const machineTotalRiskData = getMachineTotalRiskData.data ?? [];
  const strategyAndEvalListData = getStrategyAndEvaluationData.data ?? [];

  const stableStrategyData = useMemo(
    () => strategyAndEvalListData.improve_data ?? [],
    [strategyAndEvalListData.improve_data] // 의존성이 undefined면, 첫 렌더의 []를 계속 재사용
  );

  const [riskLevelDetails, setRiskLevelDetails] = useState({ isOpen: false });
  const handleRiskLevelDetailsOpen = () => setRiskLevelDetails({ isOpen: true });
  const handleRiskLevelDetailsClose = () => setRiskLevelDetails({ isOpen: false });

  // 렌더링 진입점
  return (
    <>
      <Drawer
        anchor="right"
        variant="persistent"
        open={isOpen}
        // open={fixedDrawer || isOpen}
        ModalProps={{ keepMounted: true, hideBackdrop: true }}
        sx={{
          "& .MuiDrawer-paper": {
            width: 350,
            bgcolor: alpha("#111111", 0.5),
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
            border: `1px solid ${alpha("#111111", 0.20)}`,
            top,
            height: `calc(100vh - ${top}px)`,
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* ★ 고정 헤더 영역 */}
        <Box
          id="drawer-header"
          role="presentation"
          sx={{
            py: "0.5rem",
            px: "0.875rem",

            // 선택: 헤더만 sticky로 만들고 싶으면 아래 두 줄 추가
            position: "sticky",
            top: 0,
            zIndex: 1,
            bgcolor: alpha("#111111", 0.5), // sticky 시 배경 깜빡임 방지
            backdropFilter: "blur(6px)",
            borderBottom: `1px solid ${alpha("#ffffff", 0.3)}`,
          }}
        >
          <DrawerHeader>
            <Box sx={{ fontSize: "1.35em", fontWeight: FONT_PROPS.MEDIUM, color: "#ffffff" }}>
              {`${machineInfo.safe_machine_name || machineInfo.machine_name} (연동 ID: ${currentMchId})`}
            </Box>
            <IconButton
              onClick={() => onClose?.()}
              sx={{
                color: "#ffffff",
                bgcolor: alpha("#111111", 1),
                borderRadius: 0,
                p: 0.45,
                "&&:hover": { bgcolor: alpha("#303030", 1) },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DrawerHeader>
        </Box>

    {/* ★ 스크롤 되는 본문 래퍼 */}
    <Box
      id="drawer-scroll"
      sx={{
        flex: 1,          // 남은 공간 모두 차지
        minHeight: 0,     // overflow 동작 보장
        overflowY: "auto",
        "&::-webkit-scrollbar": { display: "none" }, // 크롬/엣지
        scrollbarWidth: "none", // 파이어폭스
        "-webkit-overflow-scrolling": "touch", // 사파리(iOS 포함)
      }}
    >
      {/* 설비 종합 위험성 */}
      <CollapsibleCardTemplate title="설비 종합 위험성" isInitialOpen={true}
        containerSX={collapseContainerSX}
        titleSX={collapseTitleSX}
        iconSX={collapseIconSX}
        contentSX={collapseContentSX}
      >
        <Box id="drawer-content" role="presentation">
          <DrawerContent sx={{ p: 0 }}>
            <RiskTotalIndicator data={machineTotalRiskData.machine_total_risk ?? null} />
          </DrawerContent>
        </Box>
        <Box mt={2} p={2}>
          <MonthlyRiskTotalChart chartData={machineTotalRiskData.monthly_risks ?? null} />
        </Box>
      </CollapsibleCardTemplate>
      {/* 위험성 평가 정보 */}
      <CollapsibleCardTemplate title="위험성 평가 정보" isInitialOpen={true}
        containerSX={collapseContainerSX}
        titleSX={collapseTitleSX}
        iconSX={collapseIconSX}
        contentSX={collapseContentSX}
      >
        <Box id="drawer-risk-level-info" role="presentation" sx={{ p:2, display: 'flex', alignItems: 'center' }}>
          <Button variant="contained" size="small" onClick={handleRiskLevelDetailsOpen}
            sx={{ ml: 'auto', border: `1px solid ${alpha("#111111", 0.5)}` }}
          >
            상세정보
          </Button>
        </Box>
        <Box id="drawer-content" role="presentation">
          <DrawerContent sx={{ p: 3 }}>
            <RiskLevelIcon
              highCount={riskLowMiddleHighData.total_high_risk_count ?? 0}
              mediumCount={riskLowMiddleHighData.total_medium_risk_count ?? 0}
              lowCount={riskLowMiddleHighData.total_low_risk_count ?? 0}
            />
          </DrawerContent>
        </Box>
        <Box mt={2} p={2}>
          <MonthlyRiskEvaluationChart chartData={machineTotalRiskData.monthly_risks ?? null} />
        </Box>
      </CollapsibleCardTemplate>
      {/* 개선 대책 목록 */}
      <CollapsibleCardTemplate title="개선 대책 목록" isInitialOpen={true}
        containerSX={collapseContainerSX}
        titleSX={collapseTitleSX}
        iconSX={collapseIconSX}
        contentSX={collapseContentSX}
      >
        <RiskStrategyInfo data={stableStrategyData} />
      </CollapsibleCardTemplate>
      
      {/* <Box id="drawer-strategy-info" role="presentation" mb={2}>
        <DrawerTitle sx={{ p: 3, bgcolor: alpha("#111111", 0.5) }}>
          <Box sx={{ fontSize: "1em", fontWeight: FONT_PROPS.REGULAR, color: "#ffffff" }}>
            개선 대책 목록
          </Box>
        </DrawerTitle>
        <RiskStrategyInfo data={improveAndEvalListData.improve_data ?? []} />
      </Box> */}

      {/* 위험성 평가 목록 */}
      <CollapsibleCardTemplate title="위험성 평가 목록" isInitialOpen={true}
        containerSX={collapseContainerSX}
        titleSX={collapseTitleSX}
        iconSX={collapseIconSX}
        contentSX={collapseContentSX}
      >
        <RiskEvaluationInfo data={strategyAndEvalListData.eval_data ?? []} />
      </CollapsibleCardTemplate>
      {/* <Box id="drawer-eval-info" role="presentation" mb={2}>
        <DrawerTitle sx={{ p: 3, bgcolor: alpha("#111111", 0.5) }}>
          <Box sx={{ fontSize: "1em", fontWeight: FONT_PROPS.REGULAR, color: "#ffffff" }}>
            위험성 평가 목록
          </Box>
        </DrawerTitle>
        <RiskEvaluationInfo data={improveAndEvalListData.eval_data ?? []} />
      </Box> */}
    </Box>
  </Drawer>

  {/* 모달은 드로어 바깥에 그대로 */}
  <RiskLevelDetailsModal
    isOpen={riskLevelDetails.isOpen}
    onClose={handleRiskLevelDetailsClose}
    assetInfo={machineInfo.safe_machine_name || machineInfo.machine_name}
    data={riskLowMiddleHighData.details}
  />
    </>
  )     
};

// 상.중.하 위험 아이콘 컴포넌트
const RiskLevelIcon = ({ highCount = 0, mediumCount = 0, lowCount = 0 }) => {
  return (
    <Box sx={{ width: "100%", display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}>
      <Box display={'flex'} flexDirection={'column'} alignItems={'center'} gap={3}>
        <RiskIconBackground bgcolor={alpha("#DB0000", 1)}>
          <OutlineTextSVG 
            fontSize={22}
            fontWeight={FONT_PROPS.MEDIUM}
            color={'#fff'}
            outlinecolor={'#111111'}
            outlineWidth={1}
            text={"상"}
          />
        </RiskIconBackground>
        <OutlineTextSVG 
          fontSize={28}
          fontWeight={FONT_PROPS.MEDIUM}
          color={'#fff'}
          outlinecolor={'#111111'}
          outlineWidth={2}
          text={highCount} 
        />
      </Box>
      <Box display={'flex'} flexDirection={'column'} alignItems={'center'} gap={3}>
        <RiskIconBackground bgcolor={alpha("#FFD400", 1)}>
          <OutlineTextSVG 
            fontSize={22}
            fontWeight={FONT_PROPS.MEDIUM}
            color={'#fff'}
            outlinecolor={'#111111'}
            outlineWidth={1}
            text={"중"}
          />
        </RiskIconBackground>
        <OutlineTextSVG 
          fontSize={28}
          fontWeight={FONT_PROPS.MEDIUM}
          color={'#fff'}
          outlinecolor={'#111111'}
          outlineWidth={2}
          text={mediumCount} 
        />
      </Box>
      <Box display={'flex'} flexDirection={'column'} alignItems={'center'} gap={3}>
        <RiskIconBackground bgcolor={alpha("#00A600", 1)}>
          <OutlineTextSVG 
            fontSize={22}
            fontWeight={FONT_PROPS.MEDIUM}
            color={'#fff'}
            outlinecolor={'#111111'}
            outlineWidth={1}
            text={"하"}
          />
        </RiskIconBackground>
        <OutlineTextSVG 
          fontSize={28}
          fontWeight={FONT_PROPS.MEDIUM}
          color={'#fff'}
          outlinecolor={'#111111'}
          outlineWidth={2}
          text={lowCount} 
        />
      </Box>
    </Box>
  );
};
// 위험 아이콘 배경 컴포넌트
const RiskIconBackground = ({
  bgcolor = 'red',
  width = 45,
  height = 45,
  borderRadius = '50%',
  outlinecolor ='#787878ff',
  borderWidth = 1,
  children
}) => {
  return (
    <Box 
      sx={{ 
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: width,
        height: height,
        borderRadius: borderRadius,                 // 네모/라운드 조정
        bgcolor: bgcolor,

        // 박스 아웃라인
        //border: `${borderWidth}px solid ${alpha(outlinecolor, 0.8)}`,    // 안쪽 하얀 테두리
        boxShadow: '0 0 0 2px rgba(0,0,0,0.2)',       // 바깥 검은 아웃라인(스프레드)
      }}>
        {children}
    </Box>
  );
};
// SVG 텍스트 외곽선 컴포넌트
const OutlineTextSVG = ({ 
  fontSize= 12, 
  fontWeight= 400, 
  color= '#fff', 
  outlinecolor= '#111',
  outlineWidth= 2,
  text 
}) => {
  return (
    <svg width="28" height="28" viewBox="0 0 28 28" style={{ display: 'block' }}>
      <text
        x="50%" y="54%"
        dominantBaseline="middle" textAnchor="middle"
        fontSize={fontSize} fontWeight={fontWeight}
        fill={color}
        stroke={outlinecolor} strokeWidth={outlineWidth}
        strokeLinejoin="round"
        paintOrder="stroke"
      >
        {text}
      </text>
    </svg>
  );
};
// 월별 상.중.하 위험성 추이
const MonthlyRiskEvaluationChart = ({ chartData }) => {
  const theme = useTheme();
  const chartRef = useRef(null);

  useEffect(() => {
    const chart = echarts.init(chartRef.current);

    // 월별 데이터
    const months = chartData?.months.map(item => { return `${item}월`}); 

    // 월별로 랜덤한 위험 단계 값
    const levelData = {
      하: chartData?.high_medium_low_risks.low, // 12개로 보여주기 (ex: 1월 ~ 12월)
      중: chartData?.high_medium_low_risks.medium,
      상: chartData?.high_medium_low_risks.high,
    };

    const levelColors = {
      하: '#B8FFB8',
      중: '#FFFF00',
      상: '#FF4444'
    };

    const series = Object.keys(levelData).map(level => ({
      name: level,
      type: 'bar',
      stack: 'total',
      label: {
        show: true,
        position: 'inside',
        formatter: function (params) {
          return params.value === 0 ? '' : params.value; // 0은 표시 안함
        },
        color: '#111111',
        fontWeight: FONT_PROPS.SEMIBOLD,
        fontSize: 12,
        // textShadowColor: '#111111',
        // textShadowBlur: 5,     // 값을 키우면 더 두껍게 보입니다.
        // textShadowOffsetX: 0,
        // textShadowOffsetY: 0,
      },
      itemStyle: {
        color: levelColors[level]
      },
      data: levelData[level]
    }));

    const option = {
      title: {
        text: '월별 상.중.하 위험성 추이',
        left: 'left',
        textStyle: {
          fontFamily: FONT_PROPS.PRIMARY,
          fontWeight: FONT_PROPS.MEDIUM,
          fontSize: 13,
          color: '#ffffff',
        }
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        textStyle: {
          fontFamily: FONT_PROPS.PRIMARY,
          fontWeight: FONT_PROPS.MEDIUM,
          fontSize: 13,
          color: '#ffffff',
        },
        backgroundColor: alpha('#111111', 0.9),
        borderColor: alpha('#ffffff', 0.25),
        borderWidth: 1,
      },
      legend: {
        right: 0,
        left: 'center',   // 가로 중앙
        bottom: '2%',       // 하단 배치(숫자나 '0', '10%' 등 가능)
        orient: 'horizontal',
        textStyle: {
          fontFamily: FONT_PROPS.PRIMARY,
          fontWeight: FONT_PROPS.REGULAR,
          fontSize: 12,
          color: '#ffffff',
        },
      },
      grid: {
        top: '10%',
        left: '2%',
        right: '2%',
        bottom: '10%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: months,
        axisLabel: { // x축 레이블 설정
          fontFamily: FONT_PROPS.PRIMARY, // x축 폰트 패밀리
          fontWeight: FONT_PROPS.REGULAR, // x축 폰트 굵기
          fontSize: 12,
          color: '#ffffff',
        },
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          fontFamily: FONT_PROPS.PRIMARY, // x축 폰트 패밀리
          fontWeight: FONT_PROPS.REGULAR, // x축 폰트 굵기
          fontSize: 12,
          color: '#ffffff',
        },
      },
      series
    };

    chart.setOption(option);
    return () => chart.dispose();
  }, [chartData]);

  return (
    <Card
      sx={{ 
        width: '100%',
        height: 'auto',
        //boxShadow: '0px 1px 4px rgba(0,0,0,0.07)',
        border: `2px solid ${alpha("#111111", 0.20)}`,
        borderRadius: "0.375rem", 
        bgcolor: alpha("#111111", 0.5),
        // bgcolor: 'var(--mui-palette-background-paper)',
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 0 } }}>
        <div ref={chartRef} style={{ width: '100%', height: 500 }} />
      </CardContent>
    </Card>
  ); 
};

// 설비 종합 위험성 지표
const RiskTotalIndicator = ({ data }) => {
  const levelColors = {
    매우낮음: '#D5FFD5',
    낮음: '#B8FFB8',
    보통: '#FBFF00',
    약간높음: '#FFD5B8',
    높음: '#FF9900',
    매우높음: '#FF4444',
    미평가: '#C8C8DA'
  };

  const RiskTotalArrowIcon = ({ direction = 'up', value }) => {
    /* 화살표 방향, 상향(45도), 하향(225도), 상향 색상: #EB1515 , 하향 색상: #21CC40 , 유지 색상: #C8C8DA */
    const colorMap = {
      up: '#EB1515',
      down: '#21CC40',
      remain: '#C8C8DA'
    };
    const rotateMap = {
      up: 45,
      down: 225
    };
    return (
      <>
        {direction !== 'remain' && <ArrowUpwardIcon sx={{ transform: `rotate(${rotateMap[direction]}deg)`, color: colorMap[direction] }} />}
        {direction === 'remain' && <RemoveIcon sx={{ color: '#C8C8DA'}} />}
        <Typography
        sx={{
          fontWeight: FONT_PROPS.MEDIUM,
          fontSize: 16,
          color: colorMap[direction],
        }}
        >
          {value}
        </Typography>
      </>
    );
  };

  const machineRiskLevel = data?.monthly_risk_level;
  const riskLevelAssessment = data?.risk_level_assessment || '-';
  const yearOverYearRisk = data?.year_over_year_risk;
  const monthOverMonthRisk = data?.month_over_month_risk;

  return (
    <Box sx={{ width: '100%', height: 'auto', display: 'flex', justifyContent:'center', alignItems: 'center', gap: 4, py: 6 }}>
      <Typography 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          fontWeight: FONT_PROPS.MEDIUM, 
          fontSize: 32,
          color: levelColors[riskLevelAssessment],
          gap: 1,
        }}
      >
        {machineRiskLevel}
        <Typography component="span" fontSize={20} fontWeight={FONT_PROPS.REGULAR} color="inherit">
          ({riskLevelAssessment})
        </Typography>
      </Typography>
      <Box gap={1} sx={{ display: 'flex', flexDirection: 'column' }}>
        {/* 전년 동월 대비 */}
        <Box sx = {{ display: 'flex', alignItems: 'center' }}> 
          {/* 화살표 방향, 상향(45도), 하향(225도), 상향 색상: #EB1515 , 하향 색상: #21CC40 , 유지 색상: #C8C8DA */}  
          {yearOverYearRisk > 0 && <RiskTotalArrowIcon direction="up" value={yearOverYearRisk} />}
          {yearOverYearRisk === 0 && <RiskTotalArrowIcon direction="remain" value={yearOverYearRisk} />}
          {yearOverYearRisk < 0 && <RiskTotalArrowIcon direction="down" value={Math.abs(yearOverYearRisk)} />}
          <Typography
            sx={{
              fontWeight: FONT_PROPS.MEDIUM,
              fontSize: 16,
              color: '#ffffff',
              pl: 3,             // 왼쪽과 약간 간격
              ml: 'auto',        // 오른쪽 정렬
            }}
          >
            전년 동월 대비
          </Typography>
        </Box>
        {/* 전월 대비 */}
        <Box sx = {{ display: 'flex', alignItems: 'center' }}> 
          {/* 화살표 방향, 상향(45도), 하향(225도), 상향 색상: #EB1515 , 하향 색상: #21CC40 , 유지 색상: #C8C8DA */}  
          {monthOverMonthRisk > 0 && <RiskTotalArrowIcon direction="up" value={monthOverMonthRisk} />}
          {monthOverMonthRisk === 0 && <RiskTotalArrowIcon direction="remain" value={monthOverMonthRisk} />}
          {monthOverMonthRisk < 0 && <RiskTotalArrowIcon direction="down" value={Math.abs(monthOverMonthRisk)} />}
          <Typography
            sx={{
              fontWeight: FONT_PROPS.MEDIUM,
              fontSize: 16,
              color: '#ffffff',
              pl: 3,             // 왼쪽과 약간 간격
              ml: 'auto',        // 오른쪽 정렬
            }}
          >
            전월 대비
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
// 월별 종합 위험성 추이
const MonthlyRiskTotalChart = ({ chartData }) => {
  const theme = useTheme();
  const chartRef = useRef(null);

  /**
   * none, 1월 ~ 12월
   */
  // const [selectedMonth, setSelectedMonth] = useState('none');
  function createDiagonalPattern() {
    const canvas = document.createElement('canvas');
    canvas.width = 10;
    canvas.height = 10;
    const ctx = canvas.getContext('2d');
  
    //ctx.strokeStyle = '#999';
    ctx.strokeStyle = '#B8B8CA';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(0, 10);
    ctx.lineTo(10, 0);
    ctx.stroke();
  
    return canvas;
  }

  useEffect(() => {
    const chart = echarts.init(chartRef.current);
 
    // 월별 데이터
    const months = chartData?.months.map(item => { return `${item}월`}); 
    
    const levelColors = {
      매우낮음: '#D5FFD5',
      낮음: '#B8FFB8',
      보통: '#FBFF00',
      약간높음: '#FFD5B8',
      높음: '#FF9900',
      매우높음: '#FF4444',
      미평가: {
        image: createDiagonalPattern(), // 빗금 무늬
        repeat: 'repeat'
      }
    };
  
    // 월별로 랜덤한 위험 단계 값
    const levelData = {
      매우낮음: chartData?.total_risks.very_low,
      낮음: chartData?.total_risks.low,
      보통: chartData?.total_risks.normal,
      약간높음: chartData?.total_risks.slightly_high,
      높음: chartData?.total_risks.high,
      매우높음: chartData?.total_risks.very_high,
      미평가: chartData?.total_risks.not_evaluated,
    };
  
    const series = Object.keys(levelData).map(level => ({
      name: level,
      type: 'bar',
      stack: 'total',
      label: {
        show: true,
        position: 'inside',
        formatter: function (params) {
          return params.value === 0 ? '' : params.value; // 0은 표시 안함
        },
        color: level !== '미평가' ?  '#111111' : '#ffffff',
        fontWeight: FONT_PROPS.SEMIBOLD,
        fontSize: 12,
        // textShadowColor: '#111111',
        // textShadowBlur: 5,     // 값을 키우면 더 두껍게 보입니다.
        // textShadowOffsetX: 0,
        // textShadowOffsetY: 0,
      },
      itemStyle: {
        color: levelColors[level]
      },
      // emphasis: {
      //   focus: 'self', // ✅ 핵심: 해당 막대 조각만 강조됨
      //   itemStyle: {
      //     shadowColor: 'rgba(0, 0, 0, 0.2)',
      //     shadowBlur: 10,
      //     borderWidth: 1,
      //     borderColor: '#666'
      //   }
      // },
      data: levelData[level]
    }));
  
    const option = {
      title: {
        text: '월별 종합 위험성 추이',
        left: 'left',
        textStyle: {
          fontFamily: FONT_PROPS.PRIMARY,
          fontWeight: FONT_PROPS.MEDIUM,
          fontSize: 13,
          color: '#ffffff',
        }
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        textStyle: {
          fontFamily: FONT_PROPS.PRIMARY,
          fontWeight: FONT_PROPS.MEDIUM,
          fontSize: 13,
          color: '#ffffff',
        },
        backgroundColor: alpha('#111111', 0.9),
        borderColor: alpha('#ffffff', 0.25),
        borderWidth: 1,
      },
      legend: {
        right: 0,
        left: 'center',   // 가로 중앙
        bottom: '2%',       // 하단 배치(숫자나 '0', '10%' 등 가능)
        orient: 'horizontal',
        textStyle: {
          fontFamily: FONT_PROPS.PRIMARY,
          fontWeight: FONT_PROPS.REGULAR,
          fontSize: 12,
          color: '#ffffff',
        },
      },
      grid: {
        top: '10%',
        left: '2%',
        right: '2%',
        bottom: '14%',
        containLabel: true
      },
      xAxis: {
        type: 'value',
        axisLabel: { // x축 레이블 설정
          fontFamily: FONT_PROPS.PRIMARY, // x축 폰트 패밀리
          fontWeight: FONT_PROPS.MEDIUM, // x축 폰트 굵기
          fontSize: 12,
          color: '#ffffff',
        },
      },
      yAxis: {
        type: 'category',
        data: months,
        axisLabel: { 
          fontFamily: FONT_PROPS.PRIMARY,
          fontWeight: FONT_PROPS.MEDIUM,
          fontSize: 12,
          color: '#ffffff',
        },
      },
      series
    };
  
    chart.setOption(option);
    return () => chart.dispose();
  }, [chartData]);

  return (
    <Card
      sx={{ 
        width: '100%',
        height: 'auto',
        //mx: 'auto',
        //boxShadow: '0px 1px 4px rgba(0,0,0,0.07)',
        border: `2px solid ${alpha("#111111", 0.20)}`, 
        borderRadius: "0.375rem", 
        bgcolor: alpha("#111111", 0.5),
        //overflow: 'hidden'
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 0 } }}>
        <div ref={chartRef} style={{ width: '100%', height: 500 }} />
      </CardContent>
    </Card>
  );
};

// 개선 대책 항목 정보
const RiskStrategyInfo = ({ data }) => {
  // const tempData = [
  //   { 
  //     improve_id: 1, 
  //     improve_status: 0, // 대기 
  //     danger_description: '작업 시 벨트에 일부가 끼일 위험', 
  //     improve_plan: '',
  //   },
  //   { 
  //     improve_id: 2, 
  //     improve_status: 10, // 대책 수립
  //     danger_description: '회전부분에 의한 작업자 끼임, 감김 위험', 
  //     improve_plan: '안전가드 설치',
  //   },
  //   { 
  //     improve_id: 3, 
  //     improve_status: 20, // 대책 완료
  //     danger_description: '안전통로 미확보로 충동 및 전도 위험', 
  //     improve_plan: '안전가드 설치',
  //   },
  // ];

  // 필요 시 서버에서 이미 트리로 가공해 내려받으면 아래 두 줄은 생략 가능
  const items = useMemo(() => buildTreeFromFlat(data), [data]);

  // 개별/전체 토글 상태 (상위에서 관리)
  const [expanded, setExpanded] = useState(() =>
    Object.fromEntries(items.map((n) => [n.id, false])) // 초기 모두 접기
  );

  // items가 변하면 expanded 키도 동기화
  useEffect(() => {
    setExpanded(prev => {
      let changed = false;
      // prev를 건드리기 전에 먼저 비교하고, 필요할 때만 next를 만든다
      const next = { ...prev };

      for (const n of items) {
        if (!(n.id in next)) {
          next[n.id] = false;
          changed = true;
        }
      }
      for (const k of Object.keys(next)) {
        const exists = items.some(n => String(n.id) === String(k));
        if (!exists) {
          delete next[k];
          changed = true;
        }
      }
      return changed ? next : prev; // ← 변화 없으면 prev 그대로 반환(=렌더 안 함)
    });
  }, [items]);

  const toggleOne = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  const expandAll = () => setExpanded(Object.fromEntries(items.map((n) => [n.id, true])));
  const collapseAll = () => setExpanded(Object.fromEntries(items.map((n) => [n.id, false])));

  return(
    <Box sx={{ width: '100%', mt: 2, p: 2}}>
      {/** 범주 */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <StatusDot color={STATUS_DOT[0]} width={14} height={14} />
          <Typography component="span" fontSize={10} fontWeight={FONT_PROPS.REGULAR} color="#ffffff" ml={1}>
            {'개선 대책 수립 대기'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <StatusDot color={STATUS_DOT[10]} width={14} height={14} />
          <Typography component="span" fontSize={10} fontWeight={FONT_PROPS.REGULAR} color="#ffffff" ml={1}>
            {'개선 대책 수립'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <StatusDot color={STATUS_DOT[20]} width={14} height={14} />
          <Typography component="span" fontSize={10} fontWeight={FONT_PROPS.REGULAR} color="#ffffff" ml={1}>
            {'개선 대책 완료'}
          </Typography>
        </Box>
      </Box>

      {/** 항목 표시 */}
      <Card
        sx={{ 
          width: '100%',
          height: 'auto',
          border: `2px solid ${alpha("#111111", 0.20)}`, 
          borderRadius: "0.375rem", 
          bgcolor: alpha("#111111", 0.5),
        }}
      >
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Box sx={{ width: '100%', height: 'auto' }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, gap: 1 }}>
              <Button size="small" variant="outlined" onClick={collapseAll}>
                전체 접기
              </Button>
              <Button size="small" variant="contained" onClick={expandAll}>
                전체 펼치기
              </Button>
            </Box>
            {items.length > 0 &&
              <Box sx={{ p: 2, borderRadius: 1, width: '100%', bgcolor: alpha("#111111", 0.36) }}>
                {items.map((node) => (
                  <TreeRow
                    key={node.id}
                    node={node}
                    expanded={!!expanded[node.id]}
                    onToggle={() => toggleOne(node.id)}
                  />
                ))}
              </Box>
            }
            {items.length === 0 &&
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: 80, bgcolor: alpha("#111111", 0.36) }}>
                <Typography color={alpha("#FFFFFF", 0.7)} fontSize={13} fontWeight={FONT_PROPS.MEDIUM} textAlign="center">
                  {'개선 대책 항목이 없습니다.'}
                </Typography>
              </Box>
            }
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
// 위험성 평가 항목 정보
const RiskEvaluationInfo = ({ data= [] }) => {
  // const tempData = [
  //   { 
  //     eval_id: 1, 
  //     eval_status: 0, // 대기 
  //     danger_description: '작업 시 벨트에 일부가 끼일 위험', 
  //   },
  //   { 
  //     eval_id: 2, 
  //     eval_status: 10, // 평가 완료
  //     danger_description: '회전부분에 의한 작업자 끼임, 감김 위험', 
  //   },
  //   { 
  //     eval_id: 3, 
  //     eval_status: 20, // 승인 완료
  //     danger_description: '안전통로 미확보로 충동 및 전도 위험', 
  //   },
  // ];

  return(
    <Box sx={{ width: '100%', mt: 2, p: 2}}>
      {/** 범주 */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <StatusDot color={STATUS_DOT[0]} width={14} height={14}/>
          <Typography component="span" fontSize={10} fontWeight={FONT_PROPS.REGULAR} color="#ffffff" ml={1}>
            {'위험성 평가 대기'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <StatusDot color={STATUS_DOT[10]} width={14} height={14} />
          <Typography component="span" fontSize={10} fontWeight={FONT_PROPS.REGULAR} color="#ffffff" ml={1}>
            {'위험성 평가 완료'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <StatusDot color={STATUS_DOT[20]} width={14} height={14} />
          <Typography component="span" fontSize={10} fontWeight={FONT_PROPS.REGULAR} color="#ffffff" ml={1}>
            {'위험성 평가 승인 완료'}
          </Typography>
        </Box>
      </Box>

      {/** 항목 표시 */}
      <Card
        sx={{ 
          width: '100%',
          height: 'auto',
          border: `2px solid ${alpha("#111111", 0.20)}`, 
          borderRadius: "0.375rem", 
          bgcolor: alpha("#111111", 0.5),
        }}
      >
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Box sx={{ width: '100%', height: 'auto' }}>
            {data.length > 0 &&
              <Box sx={{ p: 2, width: '100%', minHeight: 100, maxHeight: 500, overflowY: 'auto', bgcolor: alpha("#111111", 0.36) }}>
                {data.map((item, index) => (
                  <Box key={index} sx={{ mb: 1.25 }}>
                    <Box 
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        px: 1.25,
                        py: 0.75,
                        //cursor: "pointer",
                        //userSelect: "none",
                        // "&:hover": { 
                        //   bgcolor: alpha("#FFFFFF", 0.06),
                        //   borderRadius: "9999px",
                        //   border: "1px solid",
                        //   borderColor: alpha("#FFFFFF", 0.18),
                        // }
                      }}
                    >  
                      <StatusDot color={STATUS_DOT[item.eval_status] ?? "#C8C8DA"} />

                      <Typography sx={{ fontSize: 13, fontWeight: FONT_PROPS.SEMIBOLD, mr: 1, flex: 1, minWidth: 0, color: alpha("#FFFFFF", 0.9) }}>
                        {item.danger_description}
                      </Typography>

                    </Box>
                  </Box>
                ))}
              </Box>
            }
            {data.length === 0 &&
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: 80, bgcolor: alpha("#111111", 0.36) }}>
                <Typography color={alpha("#FFFFFF", 0.7)} fontSize={13} fontWeight={FONT_PROPS.MEDIUM} textAlign="center">
                  {'위험성 평가 항목이 없습니다.'}
                </Typography>
              </Box>
            }
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

const STATUS_RANK = { 0: 1, 10: 2, 20: 3 };
const STATUS_DOT = {
  0: "#C8C8DA", // 회색
  10: "#F59E0B", // 주황(수립)
  20: "#21CC40", // 초록(완료)
};
// 상태 점 컴포넌트
const StatusDot = ({ 
  color = "#111111",
  width = 12,
  height = 12,
  borderRadius = "50%",
}) => {
  return (
    <Box
      sx={{
        width: width,
        height: height,
        borderRadius: borderRadius,
        bgcolor: color,
        boxShadow: "0 0 0 2px rgba(0,0,0,0.2) inset",
        mr: 1.25,
        flex: "0 0 auto",
      }}
    />
  );
}
/** 캡슐 모양의 부모 행 */
function CapsuleRow({ label, status, expanded, onToggle }) {
  return (
    <Box
      onClick={onToggle}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 1.25,
        py: 0.75,
        cursor: "pointer",
        //userSelect: "none",
        "&:hover": { 
          bgcolor: alpha("#FFFFFF", 0.06),
          borderRadius: "9999px",
          border: "1px solid",
          borderColor: alpha("#FFFFFF", 0.18),
        }
      }}
    >
      <IconButton
        size="small"
        onClick={(e) => { 
          e.stopPropagation();
          onToggle(); 
        }}
        sx={{
          width: 28,
          height: 28,
          color: alpha("#FFFFFF", 0.6),
          "&:hover": { bgcolor: "rgba(255,255,255,0.06)" },
        }}
      >
        {expanded ? (
          <KeyboardArrowDownRoundedIcon fontSize="small" />
        ) : (
          <KeyboardArrowRightRoundedIcon fontSize="small" />
        )}
      </IconButton>

      <StatusDot color={STATUS_DOT[status] ?? "#C8C8DA"} />

      <Typography sx={{ fontSize: 13, fontWeight: FONT_PROPS.SEMIBOLD, mr: 1, flex: 1, minWidth: 0, color: alpha("#FFFFFF", 0.9) }}>
        {label}
      </Typography>

      {/* (선택) 부모 상태 배지처럼 텍스트 노출 */}
      {/* <Typography sx={{ fontSize: 12, opacity: 0.9 }}>
        {STATUS_LABEL[status] ?? "-"}
      </Typography> */}
    </Box>
  );
}
/** L자 커넥터 라인 */
const LConnectorSVG = ({
  indent = 28 + 12 + 8, // 버튼+점+여백
  v = 16,               // 세로 길이
  h = 16,               // 가로 길이
  stroke = alpha("#FFFFFF", 0.6),
  strokeWidth = 2.5,
  radius = 4,           // 코너 라운드
  shadow = true,
}) => {
  // 라운드 코너를 가진 'ㄱ' 경로: (0,0)에서 아래로, 코너 라운드, 오른쪽으로
  const r = Math.min(radius, v, h);
  const path = `
    M 0 0
    V ${v - r}
    Q 0 ${v} ${r} ${v}
    H ${h}
  `;

  return (
    <Box sx={{ position: "relative", ml: `${indent}px`, height: v + 2, pl: 1.25 }}>
      <svg
        width={h + 2}
        height={v + 2}
        viewBox={`-1 -1 ${h + 4} ${v + 4}`}
        style={{ display: "block" }}
      >
        {shadow && (
          <filter id="l-elbow-shadow" x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="0" stdDeviation="1.2" floodOpacity="0.35" />
          </filter>
        )}
        <path
          d={path}
          fill="none"
          stroke={stroke}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
          filter={shadow ? "url(#l-elbow-shadow)" : "none"}
        />
      </svg>
    </Box>
  );
}
/** 자식(대책) 행 */
function ChildRow({ label, status }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, py: 0.25 }}>
      {/* <StatusDot color={STATUS_DOT[status] ?? "#C8C8DA"} /> */}
      <Typography
        sx={{
          fontSize: 13,
          fontWeight: FONT_PROPS.MEDIUM,
          color: alpha("#FFFFFF", 0.9),
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
        title={label}
      >
        {label}
      </Typography>
      {/* <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.65)", ml: 1 }}>
        {STATUS_LABEL[status] ?? "-"}
      </Typography> */}
    </Box>
  );
}
/** 한 부모 블록(부모+자식들) */
function TreeRow({ node, expanded, onToggle }) {
  return (
    <Box sx={{ mb: 1.25 }}>
      <CapsuleRow
        label={node.label}
        status={node.parentStatus}
        expanded={expanded}
        onToggle={onToggle}
      />
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box display={'flex'}>
          <LConnectorSVG />
          <Box sx={{ pl: 2, py: 1 }}>
            {node.children?.length ? (
              node.children.map((c) => (
                <ChildRow key={c.id} label={c.label} status={c.status} />
              ))
            ) : (
              <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.5)", py: 0.25 }}>
                개선 대책이 없습니다.
              </Typography>
            )}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}
/** tempData → 트리 스키마로 변환 (백엔드에서 가공해 보내도 OK, 프론트에서도 동일 로직 사용 가능) */
function buildTreeFromFlat(tempData) {
  const bySituation = new Map();
  tempData.forEach((row) => {
    const situation = row.danger_description;
    const status = row.improve_status;       // 0|10|20
    const plan = (row.improve_plan || "").trim();

    if (!bySituation.has(situation)) {
      bySituation.set(situation, {
        id: `sit-${bySituation.size}`, // 실제로는 DB PK나 해시 추천
        label: situation,
        parentStatus: status,
        children: [],
      });
    }

    const group = bySituation.get(situation);

    // 부모 상태는 최고 우선순위로 승격
    if (STATUS_RANK[status] > STATUS_RANK[group.parentStatus]) {
      group.parentStatus = status;
    }

    // improve_plan이 있으면 자식으로 추가
    if (plan) {
      group.children.push({
        id: row.improve_id,       // 자식 id는 해당 행 PK 사용
        label: plan,
        status,
      });
    }
  });

  return Array.from(bySituation.values());
}

////////////////////////////////////////
const columnDefs = [
  {
    field: "danger_category",
    headerName: "위험분류",
    flex: 1,
    minWidth: 140,
    headerClass: "ag-header-center",
    cellStyle: { 
      textAlign: "center",
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    },
  },
  {
    field: "danger_cause",
    headerName: "위험요인",
    flex: 1,
    minWidth: 140,
    headerClass: "ag-header-center",
    cellStyle: { 
      textAlign: "center",
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    },
  },
  {
    field: "danger_description",
    headerName: "위험요인 발생상황 및 결과",
    flex: 1,
    minWidth: 280,
    headerClass: "ag-header-center",
    cellStyle: { 
      textAlign: "center",
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    },
  },
  {
    field: "eval_risk_level",
    headerName: "위험성평가",
    flex: 1,
    minWidth: 60,
    headerClass: "ag-header-center",
    cellStyle: { 
      textAlign: "center",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    valueFormatter: (params) => {
      const level = params.value;
      switch (level) {
        case 1: return "하";
        case 2: return "중";
        case 3: return "상";
        default: return "-";
      }
    }
  },
  {
    field: "improve_plan",
    headerName: "개선대책",
    flex: 1,
    minWidth: 200,
    headerClass: "ag-header-center",
    cellStyle: { 
      textAlign: "center",
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    },
    valueFormatter: (params) => {
      return params.value ? params.value : '-';
    }
    //sort: 'asc',
  },
  {
    field: "improve_risk_level",
    headerName: "개선대책 후 위험성평가",
    flex: 1,
    minWidth: 60,
    headerClass: "ag-header-center",
    cellStyle: { 
      textAlign: "center",
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    },
    valueFormatter: (params) => {
      const level = params.value;
      switch (level) {
        case 1: return "하";
        case 2: return "중";
        case 3: return "상";
        default: return "-";
      }
    }
  },
  {
    field: "emp_name",
    headerName: "작업자",
    flex: 1,
    minWidth: 80,
    headerClass: "ag-header-center",
    cellStyle: { 
      textAlign: "center",
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    },
    valueFormatter: (params) => {
      return params.value ? params.value : '-';
    }
  },
  {
    field: "eval_status",
    headerName: "평가상태",
    flex: 1,
    minWidth: 60,
    headerClass: "ag-header-center",
    cellStyle: { 
      textAlign: "center",
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    },
    valueFormatter: (params) => {
      let text = '-';
      if(params.value === 10) { text = '평가완료'; }
      else if (params.value === 20) { text = '승인완료'; }

      return text;
    }
  },
  {
    field: "improve_status",
    headerName: "대책상태",
    flex: 1,
    minWidth: 60,
    headerClass: "ag-header-center",
    cellStyle: { 
      textAlign: "center",
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    },
    valueFormatter: (params) => {
      let text = '-';
      if(params.value === 10) { text = '등록완료'; }
      // if(params.value === 10) { text = '대책 등록완료'; }
      //else if(params.value === 20) { text = '일정 등록완료'; }
      else if(params.value === 20) { text = '개선완료'; }

      return text;
    }
  },
];
// 위험성 평가 상세정보 모달 컴포넌트
const RiskLevelDetailsModal = ({
  isOpen = false,
  onClose = null,
  assetInfo = "",
  data,
}) => {

  const gridOptions = {
    pagination: true,
    paginationPageSize: 20,
    rowHeight: 52,
    headerHeight: 52,
    suppressCellFocus: true,
    defaultColDef: {
      sortable: true,
      filter: false,
      resizable: true,
    },
    suppressMenuHide: true,
    suppressContextMenu: true,
    enableCellTextSelection: false,
  };

  return(
    <Dialog
      fullWidth
      open={isOpen}
      maxWidth="lg"
      scroll="body"
      onClose={(event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") { onClose(); }
      }}
      sx={{
        "& .MuiDialog-paper": {
          overflow: "visible",
          backgroundColor: "rgba(47, 51, 73, 0.8);",
          border: "solid 2px rgb(63 68 99)",
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
            backgroundColor: "rgba(0,0,0,0.3)",
          },
        },
      }}
    >
      <Box sx={{ borderBottom: "solid 1px #464646;", padding: "16px 24px", }}>
        <Typography variant="h3" sx={{ color: "#ced4da" }}>
          위험성 평가 목록 - {assetInfo}
        </Typography>
      </Box>
      <DialogContent
        sx={{
          px: (theme) => [
            `${theme.spacing(5)} !important`,
            `${theme.spacing(10)} !important`,
          ],
          py: (theme) => [
            `${theme.spacing(8)} !important`,
            `${theme.spacing(10)} !important`,
          ],
        }}
      >
        <div style={{ height: '500px', width: '100%' }}>
          <AgGridReact
            rowData={data}
            columnDefs={columnDefs}
            gridOptions={gridOptions}
            theme={myTheme}
            //suppressFiltersToolPanel={true}
            //suppressColumnFilter={true}
            //suppressRowClickSelection={true}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button color="primary"onClick={onClose} >
          닫기
        </Button>
      </DialogActions>
    </Dialog>
  );
};
